import React, { useState, useEffect } from 'react';
import {
  FileText, Plus, DollarSign, TrendingUp, TrendingDown,
  Download, Eye, Edit, Trash2, Calendar, PieChart,
} from 'lucide-react';
import { PieChart as RePieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import api from '../utils/api';
import { Invoice, Expense, FinancialReport, InvoiceItem } from '../types/modules';

const COLORS = ['#10B981', '#EF4444', '#F59E0B', '#8B5CF6', '#0891B2', '#EC4899'];

const EXPENSE_CATEGORIES = [
  'Ingredients',
  'Equipment',
  'Labor',
  'Utilities',
  'Marketing',
  'Other'
];

const Accounting: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('invoices');
  const [loading, setLoading] = useState<boolean>(false);
  
  // Invoices state
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [showInvoiceModal, setShowInvoiceModal] = useState<boolean>(false);
  const [invoiceForm, setInvoiceForm] = useState<{
    customer_id: string;
    customer_name: string;
    items: InvoiceItem[];
    notes: string;
    due_date: string;
  }>({
    customer_id: '',
    customer_name: '',
    items: [{ product: '', quantity: 1, unit_price: 0, total: 0 }],
    notes: '',
    due_date: '',
  });
  
  // Expenses state
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [showExpenseModal, setShowExpenseModal] = useState<boolean>(false);
  const [expenseForm, setExpenseForm] = useState<{
    category: string;
    description: string;
    amount: number;
    date: string;
    vendor: string;
    notes: string;
  }>({
    category: 'Ingredients',
    description: '',
    amount: 0,
    date: new Date().toISOString().split('T')[0],
    vendor: '',
    notes: '',
  });
  
  // Financial report state
  const [financialReport, setFinancialReport] = useState<FinancialReport | null>(null);
  const [reportPreset, setReportPreset] = useState<string>('last_30_days');

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'invoices') {
        const res = await api.get('/accounting/invoices');
        setInvoices(res.data);
      } else if (activeTab === 'expenses') {
        const res = await api.get('/accounting/expenses');
        setExpenses(res.data);
      } else if (activeTab === 'reports') {
        const res = await api.get(`/accounting/financial-report?preset=${reportPreset}`);
        setFinancialReport(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch accounting data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Invoice handlers
  const createInvoice = async () => {
    try {
      await api.post('/accounting/invoices', invoiceForm);
      setShowInvoiceModal(false);
      fetchData();
      resetInvoiceForm();
    } catch (err) {
      console.error('Failed to create invoice:', err);
      alert('Failed to create invoice');
    }
  };

  const downloadInvoicePDF = async (invoiceId, invoiceNumber) => {
    try {
      const response = await api.post(`/accounting/invoices/${invoiceId}/generate-pdf`, {}, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Failed to download PDF:', err);
      alert('Failed to download PDF');
    }
  };

  const markInvoiceAsPaid = async (invoiceId) => {
    try {
      await api.patch(`/accounting/invoices/${invoiceId}`, {
        status: 'Paid',
        payment_date: new Date().toISOString(),
      });
      fetchData();
    } catch (err) {
      console.error('Failed to update invoice:', err);
    }
  };

  const resetInvoiceForm = () => {
    setInvoiceForm({
      customer_id: '',
      customer_name: '',
      items: [{ product: '', quantity: 1, unit_price: 0, total: 0 }],
      notes: '',
      due_date: '',
    });
  };

  const addInvoiceItem = () => {
    setInvoiceForm({
      ...invoiceForm,
      items: [...invoiceForm.items, { product: '', quantity: 1, unit_price: 0, total: 0 }]
    });
  };

  const updateInvoiceItem = (index: number, field: keyof InvoiceItem, value: string | number): void => {
    const newItems = [...invoiceForm.items];
    
    // Handle type conversion based on field
    if (field === 'product') {
      newItems[index].product = value as string;
    } else if (field === 'quantity') {
      newItems[index].quantity = value as number;
    } else if (field === 'unit_price') {
      newItems[index].unit_price = value as number;
    } else if (field === 'total') {
      newItems[index].total = value as number;
    }
    
    // Recalculate total
    if (field === 'quantity' || field === 'unit_price') {
      newItems[index].total = newItems[index].quantity * newItems[index].unit_price;
    }
    
    setInvoiceForm({ ...invoiceForm, items: newItems });
  };

  // Expense handlers
  const createExpense = async () => {
    try {
      await api.post('/accounting/expenses', expenseForm);
      setShowExpenseModal(false);
      fetchData();
      resetExpenseForm();
    } catch (err) {
      console.error('Failed to create expense:', err);
      alert('Failed to create expense');
    }
  };

  const resetExpenseForm = () => {
    setExpenseForm({
      category: 'Ingredients',
      description: '',
      amount: 0,
      date: new Date().toISOString().split('T')[0],
      vendor: '',
      notes: '',
    });
  };

  const deleteExpense = async (expenseId) => {
    if (!confirm('Are you sure you want to delete this expense?')) return;
    
    try {
      await api.delete(`/accounting/expenses/${expenseId}`);
      fetchData();
    } catch (err) {
      console.error('Failed to delete expense:', err);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Accounting</h1>
        <p className="text-gray-500 mt-1">Manage invoices, expenses, and financial reports</p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="flex min-w-max sm:min-w-0">
            {[
              { id: 'invoices', label: 'Invoices', icon: FileText },
              { id: 'expenses', label: 'Expenses', icon: DollarSign },
              { id: 'reports', label: 'Financial Reports', icon: PieChart },
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-4 font-medium text-sm border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-[#0891B2] text-[#0891B2] bg-cyan-50/50'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon size={18} />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#0891B2] border-r-transparent"></div>
              <p className="text-gray-500 mt-2">Loading...</p>
            </div>
          ) : (
            <>
              {/* Invoices Tab */}
              {activeTab === 'invoices' && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold">Invoices</h3>
                    <button
                      onClick={() => setShowInvoiceModal(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-[#0891B2] hover:bg-[#0891B2]/90 text-white rounded-lg text-sm font-medium"
                    >
                      <Plus size={16} />
                      New Invoice
                    </button>
                  </div>

                  {invoices.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      No invoices yet. Create your first invoice to get started.
                    </div>
                  ) : (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Invoice #
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Customer
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Issue Date
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Due Date
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                Amount
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Status
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {invoices.map((invoice) => (
                              <tr key={invoice.id}>
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                  {invoice.invoice_number}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900">{invoice.customer_name}</td>
                                <td className="px-4 py-3 text-sm text-gray-500">
                                  {new Date(invoice.issue_date).toLocaleDateString()}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-500">
                                  {new Date(invoice.due_date).toLocaleDateString()}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                                  ${invoice.total.toFixed(2)}
                                </td>
                                <td className="px-4 py-3 text-sm">
                                  <span
                                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                      invoice.status === 'Paid'
                                        ? 'bg-green-100 text-green-800'
                                        : invoice.status === 'Overdue'
                                        ? 'bg-red-100 text-red-800'
                                        : invoice.status === 'Sent'
                                        ? 'bg-blue-100 text-blue-800'
                                        : 'bg-gray-100 text-gray-800'
                                    }`}
                                  >
                                    {invoice.status}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-right">
                                  <div className="flex items-center justify-end gap-2">
                                    <button
                                      onClick={() => downloadInvoicePDF(invoice.id, invoice.invoice_number)}
                                      className="p-1 text-gray-600 hover:text-[#0891B2]"
                                      title="Download PDF"
                                    >
                                      <Download size={16} />
                                    </button>
                                    {invoice.status !== 'Paid' && (
                                      <button
                                        onClick={() => markInvoiceAsPaid(invoice.id)}
                                        className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                                      >
                                        Mark Paid
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Expenses Tab */}
              {activeTab === 'expenses' && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold">Expenses</h3>
                    <button
                      onClick={() => setShowExpenseModal(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-[#0891B2] hover:bg-[#0891B2]/90 text-white rounded-lg text-sm font-medium"
                    >
                      <Plus size={16} />
                      New Expense
                    </button>
                  </div>

                  {expenses.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      No expenses yet. Track your first expense to get started.
                    </div>
                  ) : (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Date
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Category
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Description
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Vendor
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                Amount
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {expenses.map((expense) => (
                              <tr key={expense.id}>
                                <td className="px-4 py-3 text-sm text-gray-500">
                                  {new Date(expense.date).toLocaleDateString()}
                                </td>
                                <td className="px-4 py-3 text-sm">
                                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                    {expense.category}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900">{expense.description}</td>
                                <td className="px-4 py-3 text-sm text-gray-500">{expense.vendor || '-'}</td>
                                <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                                  ${expense.amount.toFixed(2)}
                                </td>
                                <td className="px-4 py-3 text-sm text-right">
                                  <button
                                    onClick={() => deleteExpense(expense.id)}
                                    className="p-1 text-gray-600 hover:text-red-600"
                                    title="Delete"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Financial Reports Tab */}
              {activeTab === 'reports' && financialReport && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold">Financial Report (P&L)</h3>
                    <select
                      value={reportPreset}
                      onChange={(e) => {
                        setReportPreset(e.target.value);
                        setTimeout(fetchData, 100);
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent text-sm"
                    >
                      <option value="today">Today</option>
                      <option value="last_7_days">Last 7 Days</option>
                      <option value="last_30_days">Last 30 Days</option>
                      <option value="last_90_days">Last 90 Days</option>
                    </select>
                  </div>

                  {/* KPI Cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                      <div className="flex items-center gap-2 mb-1">
                        <TrendingUp size={18} className="text-green-600" />
                        <p className="text-sm text-gray-600">Revenue</p>
                      </div>
                      <p className="text-2xl font-bold text-gray-900">
                        ${financialReport.revenue.toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4 border border-red-200">
                      <div className="flex items-center gap-2 mb-1">
                        <TrendingDown size={18} className="text-red-600" />
                        <p className="text-sm text-gray-600">Expenses</p>
                      </div>
                      <p className="text-2xl font-bold text-gray-900">
                        ${financialReport.expenses.toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                      <p className="text-sm text-gray-600 mb-1">Gross Profit</p>
                      <p className="text-2xl font-bold text-gray-900">
                        ${financialReport.gross_profit.toLocaleString()}
                      </p>
                    </div>
                    <div className={`bg-gradient-to-br rounded-lg p-4 border ${
                      financialReport.net_profit >= 0
                        ? 'from-cyan-50 to-cyan-100 border-cyan-200'
                        : 'from-orange-50 to-orange-100 border-orange-200'
                    }`}>
                      <p className="text-sm text-gray-600 mb-1">Net Profit</p>
                      <p className="text-2xl font-bold text-gray-900">
                        ${financialReport.net_profit.toLocaleString()}
                      </p>
                    </div>
                  </div>

                  {/* Charts */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Expense Breakdown */}
                    {Object.keys(financialReport.expense_breakdown).length > 0 && (
                      <div className="border border-gray-200 rounded-lg p-4">
                        <h3 className="text-lg font-semibold mb-4">Expenses by Category</h3>
                        <ResponsiveContainer width="100%" height={300}>
                          <RePieChart>
                            <Pie
                              data={Object.entries(financialReport.expense_breakdown).map(([key, value]) => ({
                                name: key,
                                value: value,
                              }))}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              outerRadius={100}
                              label
                            >
                              {Object.keys(financialReport.expense_breakdown).map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                          </RePieChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    {/* Revenue vs Expenses */}
                    <div className="border border-gray-200 rounded-lg p-4">
                      <h3 className="text-lg font-semibold mb-4">Revenue vs Expenses</h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={[
                            { name: 'Revenue', value: financialReport.revenue },
                            { name: 'Expenses', value: financialReport.expenses },
                            { name: 'Net Profit', value: financialReport.net_profit },
                          ]}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="value" fill="#0891B2" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Create Invoice Modal */}
      {showInvoiceModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold">Create Invoice</h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer Name *
                  </label>
                  <input
                    type="text"
                    value={invoiceForm.customer_name}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, customer_name: e.target.value, customer_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Due Date *
                  </label>
                  <input
                    type="date"
                    value={invoiceForm.due_date}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, due_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Line Items</label>
                {invoiceForm.items.map((item, index) => (
                  <div key={index} className="grid grid-cols-4 gap-2 mb-2">
                    <input
                      type="text"
                      placeholder="Product"
                      value={item.product}
                      onChange={(e) => updateInvoiceItem(index, 'product', e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <input
                      type="number"
                      placeholder="Qty"
                      value={item.quantity}
                      onChange={(e) => updateInvoiceItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <input
                      type="number"
                      placeholder="Price"
                      value={item.unit_price}
                      onChange={(e) => updateInvoiceItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <input
                      type="number"
                      placeholder="Total"
                      value={item.total}
                      readOnly
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50"
                    />
                  </div>
                ))}
                <button
                  onClick={addInvoiceItem}
                  className="text-sm text-[#0891B2] hover:text-[#0891B2]/80 font-medium"
                >
                  + Add Item
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={invoiceForm.notes}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowInvoiceModal(false);
                  resetInvoiceForm();
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={createInvoice}
                className="px-4 py-2 bg-[#0891B2] hover:bg-[#0891B2]/90 text-white rounded-lg"
              >
                Create Invoice
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Expense Modal */}
      {showExpenseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold">Create Expense</h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                  <select
                    value={expenseForm.category}
                    onChange={(e) => setExpenseForm({ ...expenseForm, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent"
                  >
                    {EXPENSE_CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                  <input
                    type="number"
                    value={expenseForm.amount}
                    onChange={(e) => setExpenseForm({ ...expenseForm, amount: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <input
                  type="text"
                  value={expenseForm.description}
                  onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                  <input
                    type="date"
                    value={expenseForm.date}
                    onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vendor</label>
                  <input
                    type="text"
                    value={expenseForm.vendor}
                    onChange={(e) => setExpenseForm({ ...expenseForm, vendor: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={expenseForm.notes}
                  onChange={(e) => setExpenseForm({ ...expenseForm, notes: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowExpenseModal(false);
                  resetExpenseForm();
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={createExpense}
                className="px-4 py-2 bg-[#0891B2] hover:bg-[#0891B2]/90 text-white rounded-lg"
              >
                Create Expense
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Accounting;
